import type * as api from '@gitbook/api-client';
import { NavigationRevisionCurrent } from '@gitbook/app-navigation';

import type { NavigationTarget } from '../../navigation';

/**
 * What a conversation edits in.
 *
 * The two things the application itself edits through, and so the two the agent can be pointed at: a
 * change request, or — when an admin has unlocked its live edits — the space itself, written to in
 * place with nothing to review or merge.
 *
 * The change request is carried as whichever identifier the caller has: the route knows its number,
 * the agent reports its id, and everything either is handed to resolves both.
 */
export type AgentEditTarget =
    | {
          kind: 'change_request';
          spaceId: string;
          changeRequest: string | number;
      }
    | { kind: 'space'; spaceId: string };

/**
 * An edit the agent made, as far as naming where it landed goes.
 */
export type AgentEditLocation = {
    spaceId: string;
    /** Absent for an edit made in a space directly, and from an agent that predates reporting it. */
    changeRequestId?: string;
};

/**
 * The target an agent reports, in the shape the UI addresses it by.
 */
export function getAgentEditTarget(
    reported: api.AgentEditTarget | null | undefined
): AgentEditTarget | null {
    if (!reported) {
        return null;
    }

    return reported.changeRequestId
        ? {
              kind: 'change_request',
              spaceId: reported.spaceId,
              changeRequest: reported.changeRequestId,
          }
        : { kind: 'space', spaceId: reported.spaceId };
}

/**
 * Where to open a target: the change request's overview, or the space's editor — or, given `diff`,
 * one of its pages compared against the revision the agent started from.
 *
 * Every key is spelled out, `undefined` included: navigating within the same space merges the
 * modifier over the current screen, so an omitted key would leave whatever the user is looking at —
 * another change request, a revision, an open side sheet — in place.
 */
export function getEditTargetNavigationTarget(
    target: AgentEditTarget,
    diff?: { pagePath: string | undefined; baseRevisionId: string }
): NavigationTarget {
    const page = diff?.pagePath ?? '';

    if (target.kind === 'change_request') {
        return {
            current: {
                type: 'space',
                space: target.spaceId,
                changeRequest: target.changeRequest,
                page,
                mode: diff ? 'diff' : undefined,
                revision: diff ? NavigationRevisionCurrent : undefined,
                baseRevision: diff?.baseRevisionId,
                sideSheet: undefined,
            },
        };
    }

    return {
        current: {
            type: 'space',
            space: target.spaceId,
            changeRequest: undefined,
            page,
            mode: diff ? 'diff' : undefined,
            revision: diff ? NavigationRevisionCurrent : undefined,
            baseRevision: diff?.baseRevisionId,
            sideSheet: undefined,
        },
    };
}

/**
 * Whether an edit was made in the target the conversation is working in now.
 *
 * A conversation can be pointed at another change request part-way through, and the pages it edited
 * before that belong to the previous one — listing them together is what made the widget read as
 * unrelated to the change request in front of the user. An edit that reports no change request is
 * matched on its space alone: that is either a space edited live, or an agent that predates
 * reporting it, and neither can be told apart from the edit itself.
 */
export function isEditInTarget(edit: AgentEditLocation, target: AgentEditTarget): boolean {
    if (edit.spaceId !== target.spaceId) {
        return false;
    }

    if (!edit.changeRequestId || target.kind !== 'change_request') {
        return true;
    }

    // A target addressed by number came from a URL rather than from the agent, and an edit only
    // ever names an id: the space is as far as they can be compared.
    if (typeof target.changeRequest === 'number') {
        return true;
    }

    return edit.changeRequestId === target.changeRequest;
}
